/**
 * Tartı Servisi — local-service köprüsü
 *
 * Tarayıcı ham TCP soketi açamadığı için tartıyla konuşma işi local-service'e
 * ait (handlers/scale-handler.js). Bu dosya onun HTTP yüzeyini sarar.
 *
 * HEDEF HER İSTEKTE GİDER. local-service hangi tartıların var olduğunu bilmez;
 * tanım SQL'deki `Scales` tablosunda durur (bkz. services/scaleService.ts) ve
 * buradan host/port/model olarak taşınır. Böylece aynı bilgisayar birden fazla
 * tartıyla konuşabilir.
 *
 * Canlı ağırlık bu servisten değil, `useScaleWeight` hook'undaki SSE
 * bağlantısından gelir; buradaki `readScaleWeight` tek seferlik okumalar
 * (bağlantı testi, "tek sorgu" okuma modu) içindir.
 *
 * Hata sözleşmesi localPrintService ile aynı: sunucu `errorCode` döndürür,
 * burada kullanıcıya gösterilebilir Türkçe mesaja çevrilir.
 */

import axios, { AxiosError } from 'axios';

export const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';
export const DEFAULT_SCALE_PORT = 20304;

const REQUEST_TIMEOUT_MS = 8000;

/** Konuşulacak cihaz. Kaynağı `Scales` tablosundaki kayıt. */
export interface ScaleTarget {
  host: string;
  port?: number;
  /** Protokol sürücüsü; bugün tek değer: "CAS_CL". */
  model?: string;
}

/** Tartıdan gelen tek bir ağırlık okuması. */
export interface ScaleReading {
  /** Protokoldeki ham hex alan — hata ayıklama için taşınıyor. */
  raw: string;
  /** İşaretli ham birim (gram). Negatif olabilir: dara alınmış kap kaldırıldığında. */
  units: number;
  decimals: number;
  kg: number;
  price: string | null;
  /** Ardışık eşit okumayla belirlenir; cihazda kararlılık biti yok. */
  stable?: boolean;
  at?: string;
}

export interface ScaleInfo {
  scaleId: string | null;
  departmentId: string | null;
  weightDecimals: number;
  priceDecimals: number;
  unit: 'kg' | 'lb';
  capacityKg: number | null;
}

export interface ScaleStatus {
  key: string;
  host?: string;
  port?: number;
  model?: string;
  connected: boolean;
  polling: boolean;
  listeners: number;
  info: ScaleInfo | null;
  lastReading: ScaleReading | null;
  lastError: string | null;
}

export class LocalScaleError extends Error {
  errorCode: string;
  constructor(message: string, errorCode: string) {
    super(message);
    this.name = 'LocalScaleError';
    this.errorCode = errorCode;
  }
}

export function mapScaleErrorMessage(errorCode?: string, serverMessage?: string): string {
  switch (errorCode) {
    case 'SCALE_NOT_CONFIGURED':
      return 'Tartı seçilmemiş. Hızlı Satış ayarlarından bir tartı seçin.';
    case 'SCALE_INVALID_PLU':
      return 'Tartı kayıt numarası geçersiz.';
    case 'SCALE_INVALID_ITEM_CODE':
      return 'Tartı kodu geçersiz. Ürün kartındaki tartı kodunu kontrol edin.';
    case 'SCALE_PLU_REJECTED':
      return 'Tartı ürünü kabul etmedi. Cihazın hafızası dolu olabilir.';
    case 'SCALE_INVALID_TARGET':
      return 'Tartının IP adresi veya portu geçersiz. Tartı tanımını kontrol edin.';
    case 'SCALE_OFFLINE':
      return 'Tartıya ulaşılamıyor. Cihazın açık ve ağa bağlı olduğundan emin olun.';
    case 'SCALE_TIMEOUT':
      return 'Tartı yanıt vermedi. Bağlantıyı kontrol edin.';
    case 'SCALE_PROTOCOL_ERROR':
      return 'Tartıdan beklenmeyen bir yanıt geldi.';
    case 'SERVICE_UNREACHABLE':
      return 'Yerel servise ulaşılamadı. AQUASOFT uygulamasının açık olduğundan emin olun.';
    case 'SERVICE_OUTDATED':
      return 'Yerel servis bu işlemi tanımıyor. AQUASOFT uygulamasını kapatıp yeniden açın; sürüm eskiyse güncelleyin.';
    default:
      return serverMessage || 'Tartı ile iletişimde bir hata oluştu.';
  }
}

function baseUrlOf(localServiceUrl?: string | null): string {
  return localServiceUrl || DEFAULT_LOCAL_SERVICE_URL;
}

function targetParams(target: ScaleTarget): string {
  const params = new URLSearchParams({
    host: target.host,
    port: String(target.port || DEFAULT_SCALE_PORT),
  });
  if (target.model) params.set('model', target.model);
  return params.toString();
}

/** SSE uç noktası — `useScaleWeight` bu adrese bağlanır. */
export function scaleEventsUrl(target: ScaleTarget, localServiceUrl?: string | null): string {
  return `${baseUrlOf(localServiceUrl)}/api/scale/events?${targetParams(target)}`;
}

function toScaleError(err: unknown): LocalScaleError {
  const ax = err as AxiosError<any>;
  if (ax && ax.isAxiosError && !ax.response) {
    return new LocalScaleError(
      mapScaleErrorMessage('SERVICE_UNREACHABLE'),
      'SERVICE_UNREACHABLE'
    );
  }
  // Servis ayakta ama uç yok: eski sürüm çalışıyor. Ayırt etmezsek kullanıcı
  // genel bir "iletişim hatası" görüp cihazı suçluyor.
  if (ax?.response?.status === 404) {
    return new LocalScaleError(mapScaleErrorMessage('SERVICE_OUTDATED'), 'SERVICE_OUTDATED');
  }
  const data = ax?.response?.data;
  const code = data?.errorCode;
  return new LocalScaleError(mapScaleErrorMessage(code, data?.error), code || 'SCALE_ERROR');
}

/**
 * Tek seferlik ağırlık okuma. Canlı takip için `useScaleWeight` kullanın:
 * bu uç her çağrıda tartıya ayrı bir sorgu gönderir.
 */
export async function readScaleWeight(
  target: ScaleTarget,
  localServiceUrl?: string | null
): Promise<ScaleReading> {
  try {
    const { data } = await axios.get(
      `${baseUrlOf(localServiceUrl)}/api/scale/weight?${targetParams(target)}`,
      { timeout: REQUEST_TIMEOUT_MS }
    );
    if (!data?.success) {
      throw new LocalScaleError(
        mapScaleErrorMessage(data?.errorCode, data?.error),
        data?.errorCode || 'SCALE_ERROR'
      );
    }
    return data.data as ScaleReading;
  } catch (err) {
    if (err instanceof LocalScaleError) throw err;
    throw toScaleError(err);
  }
}

export async function getScaleStatus(
  target: ScaleTarget,
  localServiceUrl?: string | null
): Promise<ScaleStatus> {
  try {
    const { data } = await axios.get(
      `${baseUrlOf(localServiceUrl)}/api/scale/status?${targetParams(target)}`,
      { timeout: REQUEST_TIMEOUT_MS }
    );
    return data.data as ScaleStatus;
  } catch (err) {
    throw toScaleError(err);
  }
}

/**
 * Bağlantıyı dener. Tartının kimliğini ve anlık ağırlığını birlikte döndürür;
 * kullanıcı doğru cihaza bağlandığını böyle görür.
 */
export async function testScaleConnection(
  target: ScaleTarget,
  localServiceUrl?: string | null
): Promise<{ info: ScaleInfo; weight: ScaleReading }> {
  try {
    const { data } = await axios.post(
      `${baseUrlOf(localServiceUrl)}/api/scale/test`,
      { host: target.host, port: target.port || DEFAULT_SCALE_PORT, model: target.model },
      { timeout: REQUEST_TIMEOUT_MS }
    );
    if (!data?.success) {
      throw new LocalScaleError(
        mapScaleErrorMessage(data?.errorCode, data?.error),
        data?.errorCode || 'SCALE_ERROR'
      );
    }
    return data.data;
  } catch (err) {
    if (err instanceof LocalScaleError) throw err;
    throw toScaleError(err);
  }
}

/** Tartıya gönderilecek tek ürün. */
export interface ScalePluPayload {
  /** Cihazdaki kayıt numarası — tartıda tuşla çağırmak için. */
  pluNumber: number;
  /**
   * Ağırlık barkoduna basılacak kod (cihazın ItemCode alanı).
   * Kasada barkod çözülünce ürün bu koda göre bulunuyor.
   */
  itemCode: number;
  /** Ürün adı; cihaz 40 karakter tutuyor, fazlası kırpılır. */
  name: string;
  /** Birim fiyat, TL. */
  price: number;
  /** Raf ömrü, gün. "Bugün dahil N gün"; 0/boş ise etikete tarih basılmaz. */
  sellByDays?: number;
  /** Cihazdaki departman; tanımlı tek departman varsa 1. */
  department?: number;
}

/**
 * Tek ürünü tartıya yazar.
 *
 * Toplu gönderim bilerek burada değil çağıran tarafta sıralı döngüyle yapılıyor:
 * ilerleme çubuğu böyle doğal çalışıyor ve bir ürünün hatası kalan ürünleri
 * durdurmuyor. local-service zaten cihaz başına komut kuyruğu tutuyor.
 */
export async function sendPluToScale(
  target: ScaleTarget,
  plu: ScalePluPayload,
  localServiceUrl?: string | null
): Promise<void> {
  try {
    const { data } = await axios.post(
      `${baseUrlOf(localServiceUrl)}/api/scale/plu`,
      {
        host: target.host,
        port: target.port || DEFAULT_SCALE_PORT,
        model: target.model,
        plu,
      },
      { timeout: REQUEST_TIMEOUT_MS }
    );
    if (!data?.success) {
      throw new LocalScaleError(
        mapScaleErrorMessage(data?.errorCode, data?.error),
        data?.errorCode || 'SCALE_ERROR'
      );
    }
  } catch (err) {
    if (err instanceof LocalScaleError) throw err;
    throw toScaleError(err);
  }
}

/**
 * Tartıdaki bir kaydı siler.
 *
 * Toplu temizlik bilerek çağıran tarafta sıralı döngüyle yapılıyor: hangi
 * kaydın silindiği görülebilsin ve iş yarıda durdurulabilsin. Cihazın
 * "hepsini sil" komutu var ama kasıtlı olarak hiç uygulanmadı — tek yanlış
 * çağrı bütün ürünleri geri dönüşü olmadan siler.
 */
export async function deletePluFromScale(
  target: ScaleTarget,
  pluNumber: number,
  department = 1,
  localServiceUrl?: string | null
): Promise<void> {
  try {
    const { data } = await axios.post(
      `${baseUrlOf(localServiceUrl)}/api/scale/plu/delete`,
      {
        host: target.host,
        port: target.port || DEFAULT_SCALE_PORT,
        model: target.model,
        pluNumber,
        department,
      },
      { timeout: REQUEST_TIMEOUT_MS }
    );
    if (!data?.success) {
      throw new LocalScaleError(
        mapScaleErrorMessage(data?.errorCode, data?.error),
        data?.errorCode || 'SCALE_ERROR'
      );
    }
  } catch (err) {
    if (err instanceof LocalScaleError) throw err;
    throw toScaleError(err);
  }
}

/** Cihazdan okunan bir PLU kaydı. */
export interface ScalePluRecord {
  department: number;
  pluNumber: number;
  pluType?: number;
  name: string;
  itemCode: number;
  price: number | null;
  priceUnits?: number;
  group?: number;
  sellByDays?: number;
}

export interface ScaleScanResult {
  from: number;
  to: number;
  scanned: number;
  found: ScalePluRecord[];
}

/**
 * Tek seferde taranabilecek numara sayısı.
 *
 * Cihazda "hepsini listele" komutu yok; numaralar tek tek sorgulanıyor.
 * CL3000'de ölçülen okuma süresi ~20 ms, yani 1000 numara ~20 saniye.
 * local-service aynı sınırı uyguluyor.
 */
export const MAX_SCAN_RANGE = 1000;

/**
 * Cihazdaki kayıtları tarar.
 *
 * Aralık bilinçli olarak zorunlu: 10.000 PLU'yu baştan sona okumak dakikalar
 * sürer. Kullanıcı pratikte ilk birkaç yüz numarayı kullanıyor.
 */
export async function scanScalePlus(
  target: ScaleTarget,
  from: number,
  to: number,
  department = 1,
  localServiceUrl?: string | null
): Promise<ScaleScanResult> {
  try {
    const params = new URLSearchParams({
      host: target.host,
      port: String(target.port || DEFAULT_SCALE_PORT),
      from: String(from),
      to: String(to),
      department: String(department),
    });
    if (target.model) params.set('model', target.model);

    const { data } = await axios.get(
      `${baseUrlOf(localServiceUrl)}/api/scale/plu/scan?${params.toString()}`,
      // Tarama okuma başına ~50 ms; 250 kayıt için varsayılan 8 sn yetmiyor.
      { timeout: 90000 }
    );
    if (!data?.success) {
      throw new LocalScaleError(
        mapScaleErrorMessage(data?.errorCode, data?.error),
        data?.errorCode || 'SCALE_ERROR'
      );
    }
    return data.data as ScaleScanResult;
  } catch (err) {
    if (err instanceof LocalScaleError) throw err;
    throw toScaleError(err);
  }
}

/** Tartıdaki kaydı geri okur. Doğrulama ve tanılama için. */
export async function readPluFromScale(
  target: ScaleTarget,
  pluNumber: number,
  department = 1,
  localServiceUrl?: string | null
): Promise<ScalePluPayload | null> {
  try {
    const params = new URLSearchParams({
      host: target.host,
      port: String(target.port || DEFAULT_SCALE_PORT),
      pluNumber: String(pluNumber),
      department: String(department),
    });
    if (target.model) params.set('model', target.model);

    const { data } = await axios.get(
      `${baseUrlOf(localServiceUrl)}/api/scale/plu?${params.toString()}`,
      { timeout: REQUEST_TIMEOUT_MS }
    );
    if (!data?.success) {
      throw new LocalScaleError(
        mapScaleErrorMessage(data?.errorCode, data?.error),
        data?.errorCode || 'SCALE_ERROR'
      );
    }
    return data.data || null;
  } catch (err) {
    if (err instanceof LocalScaleError) throw err;
    throw toScaleError(err);
  }
}
